import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spin, Typography, Row, Col, Statistic, Tooltip, DatePicker, Segmented, Space, Empty } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, SwapOutlined, AccountBookOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { walletService } from '../../common/services';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend } from 'recharts';

dayjs.locale('fr');
const { Title } = Typography;
const { RangePicker } = DatePicker;

const Wallet = () => {
    const [soldeWallet, setSoldeWallet] = useState(null);
    const [flux, setFlux] = useState({ entrees: 0, sorties: 0, net: 0 });
    const [isLoading, setLoading] = useState(true);

    // Contrôles du graphe
    const [granularity, setGranularity] = useState('day'); // 'day' | 'week' | 'month'
    const [range, setRange] = useState([
        dayjs().subtract(29, 'day').startOf('day'),
        dayjs().endOf('day'),
    ]);
    const [evolution, setEvolution] = useState([]); // [{date, entrees, sorties, net}]

    // Chargement des KPI de tête + premier fetch du graphe
    useEffect(() => {
        const loadKpis = async () => {
            setLoading(true);
            try {
                const [solde, fluxRes] = await Promise.all([
                    walletService.getSoldeWallet(),
                    walletService.getFluxWallet({ from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') }),
                ]);
                setSoldeWallet(Number(solde?.solde ?? 0));
                setFlux({
                    entrees: Number(fluxRes?.entrees ?? 0),
                    sorties: Number(fluxRes?.sorties ?? 0),
                    net: Number(fluxRes?.net ?? 0),
                });
            } catch (e) {
                console.error('Erreur API (KPIs):', e);
            } finally {
                setLoading(false);
            }
        };
        loadKpis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch graphe à chaque changement de période / granularité
    useEffect(() => {
        const loadEvolution = async () => {
            try {
                const resp = await walletService.getEvolutionWallet({
                    from: range[0].format('YYYY-MM-DD'),
                    to: range[1].format('YYYY-MM-DD'),
                    granularity,
                });
                // resp => { unit, data: [{date, entrees, sorties, net}] }
                setEvolution(Array.isArray(resp?.data) ? resp.data : []);
            } catch (e) {
                console.error('Erreur API (Evolution):', e);
                setEvolution([]);
            }
        };
        loadEvolution();
    }, [granularity, range]);

    // Formatage pour l’axe X
    const formatLabel = (isoDate) => {
        const d = dayjs(isoDate);
        if (!d.isValid()) return '';
        if (granularity === 'day') return d.format('DD MMM');
        if (granularity === 'week') return `S${d.week?.() ?? d.format('WW')} ${d.format('YYYY')}`;
        return d.format('MMM YYYY'); // month
    };

    const chartData = useMemo(
        () => evolution.map((r) => ({
            x: formatLabel(r.date),
            entrees: Number(r.entrees || 0),
            sorties: Number(r.sorties || 0),
            net: Number(r.net || 0),
        })),
        [evolution, granularity]
    );

    const fmt = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());
    const netColor = flux.net > 0 ? '#52c41a' : flux.net < 0 ? '#f5222d' : 'inherit';
    const soldeColor = (soldeWallet ?? 0) >= 0 ? '#52c41a' : '#f5222d';

    const numberFmt = (n) => new Intl.NumberFormat().format(n);

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const e = payload.find(p => p.dataKey === 'entrees');
        const s = payload.find(p => p.dataKey === 'sorties');
        const n = payload.find(p => p.dataKey === 'net');
        return (
            <Card size="small" style={{ pointerEvents: 'none' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                <div>Entrées : <b>{numberFmt(e?.value || 0)}</b> pts</div>
                <div>Sorties : <b>{numberFmt(s?.value || 0)}</b> pts</div>
                <div>Net : <b>{numberFmt(n?.value || 0)}</b> pts</div>
            </Card>
        );
    };

    // en haut du fichier (après les imports)
    const COLORS = {
        entrees: '#52c41a', // vert Ant Design
        sorties: '#f5222d', // rouge Ant Design
        net: '#1677ff',     // bleu (net)
    };

    


    return (
        <Spin spinning={isLoading}>
            <div className="content-wrapper">
                <div className="container-xxl flex-grow-1 container-p-y">
                    <div className="card mb-4" style={{ padding: 24 }}>
                        <Title level={2}>Solde du Wallet</Title>

                        {/* ---- Ligne des 4 KPIs ---- */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={12} md={12} lg={6}>
                                <Card hoverable bordered bodyStyle={{ padding: 16 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Title level={5} style={{ margin: 0 }}>Sorties</Title>
                                        <Tooltip title="Points donnés aux chauffeurs (débit du wallet)">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                    <Statistic
                                        value={flux.sorties}
                                        suffix=" pts"
                                        prefix={<ArrowDownOutlined />}
                                        valueStyle={{ color: '#f5222d' }}
                                        formatter={(val) => fmt(val)}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={6}>
                                <Card hoverable bordered bodyStyle={{ padding: 16 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Title level={5} style={{ margin: 0 }}>Entrées</Title>
                                        <Tooltip title="Points retirés aux chauffeurs (crédit du wallet)">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                    <Statistic
                                        value={flux.entrees}
                                        suffix=" pts"
                                        prefix={<ArrowUpOutlined />}
                                        valueStyle={{ color: '#52c41a' }}
                                        formatter={(val) => fmt(val)}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={6}>
                                <Card hoverable bordered bodyStyle={{ padding: 16 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Title level={5} style={{ margin: 0 }}>Net</Title>
                                        <Tooltip title="Entrées - Sorties (sur la période ci-dessous)">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                    <Statistic
                                        value={flux.net}
                                        suffix=" pts"
                                        prefix={<SwapOutlined />}
                                        valueStyle={{ color: netColor }}
                                        formatter={(val) => fmt(val)}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={6}>
                                <Card hoverable bordered bodyStyle={{ padding: 16 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Title level={5} style={{ margin: 0 }}>Solde actuel</Title>
                                        <Tooltip title="Solde courant du wallet">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                    <Statistic
                                        value={soldeWallet}
                                        suffix=" pts"
                                        prefix={<AccountBookOutlined />}
                                        valueStyle={{ color: soldeColor }}
                                        formatter={(val) => fmt(val)}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* ---- Contrôles & Graphe ---- */}
                        <Card
                            title="Évolution du wallet"
                            extra={
                                <Space size="middle" wrap>
                                    <Segmented
                                        options={[
                                            { label: 'Jour', value: 'day' },
                                            { label: 'Semaine', value: 'week' },
                                            { label: 'Mois', value: 'month' },
                                        ]}
                                        value={granularity}
                                        onChange={(v) => setGranularity(v)}
                                    />
                                    <RangePicker
                                        value={range}
                                        onChange={(vals) => {
                                            if (!vals || vals.length !== 2) return;
                                            setRange([vals[0].startOf('day'), vals[1].endOf('day')]);
                                        }}
                                        allowClear={false}
                                    />
                                </Space>
                            }
                            bodyStyle={{ padding: 16 }}
                        >
                            {chartData.length === 0 ? (
                                <Empty description="Aucune donnée sur la période" />
                            ) : (
                                <div style={{ width: '100%', height: 360 }}>
                                    <ResponsiveContainer>
                                        <ComposedChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="x" />
                                            <YAxis tickFormatter={numberFmt} />
                                            <RTooltip content={<CustomTooltip />} />
                                            <Legend />
                                            {/* Sorties en rouge */}
                                            <Bar
                                                dataKey="sorties"
                                                name="Sorties"
                                                stackId="a"
                                                fill={COLORS.sorties}
                                                stroke={COLORS.sorties}
                                                barSize={18}
                                            />
                                            {/* Entrées en vert */}
                                            <Bar
                                                dataKey="entrees"
                                                name="Entrées"
                                                stackId="a"
                                                fill={COLORS.entrees}
                                                stroke={COLORS.entrees}
                                                barSize={18}
                                            />
                                            {/* Net en bleu */}
                                            <Line
                                                dataKey="net"
                                                name="Net"
                                                dot={false}
                                                strokeWidth={2}
                                                stroke={COLORS.net}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </Card>

                    </div>
                </div>
            </div>
        </Spin>
    );
};

export default Wallet;
